<script lang="ts">
import { defineComponent, inject, reactive, computed, onMounted } from 'vue'
import ButtonDefault from '@/components/button/ButtonDefault.vue'
import SelectorLite from '@/components/form/SelectorLite.vue'
import PaginationDefault from '@/components/pagination/PaginationDefault.vue'
import FollowGuide from '@/components/follow/FollowGuide.vue'
import SectionHeadingDefault from '@/components/heading/SectionHeadingDefault.vue'
import TextTruncationMultiLine from '@/components/formatter/TextTruncationMultiLine.vue'
import TextTruncationSingleLine from '@/components/formatter/TextTruncationSingleLine.vue'
import stateInitializer from '@/models/stateInitializer'
import paginationModel from '@/models/pagination'
import { useMediaQueryStore } from '@/stores/mediaQuery'
import { useScreenLockStore } from '@/stores/screenLock'
import { keywordPath } from '@/generated/railsRoutes'

const PAGINATION_QUERY = 'page'

export default defineComponent({
  name: 'KeywordsMain',

  components: {
    ButtonDefault,
    SelectorLite,
    PaginationDefault,
    FollowGuide,
    SectionHeadingDefault,
    TextTruncationMultiLine,
    TextTruncationSingleLine,
  },

  setup() {
    // constants
    const KEYWORD_CATEGORIES = inject<Record<string, string>>('KEYWORD_CATEGORIES') || {}

    interface State {
      selectedCategory: {
        id: Maybe<number>,
        label: string,
        description: string,
        items: {
          id: number,
          label: string,
        }[],
      }
      pagination: Pagination,
      trendFilterKeywords: Pick<KeywordItem, 'id' | 'title'>[],
    }

    const state: State = reactive({
      selectedCategory: {
        id: undefined,
        label: '',
        description: '',
        items: [],
      },
      pagination: {
        page: 0,
        per: 0,
        totalCount: 0,
      },
      trendFilterKeywords: [],
    })

    stateInitializer(state, 'main')
    const { pagination } = paginationModel(state.pagination)

    // store
    const mediaQueryStore = useMediaQueryStore()
    const screenLock = useScreenLockStore()

    const categorySelectorItems = computed(() => {
      return Object.keys(KEYWORD_CATEGORIES).map(key => {
        return {
          value: Number(key),
          label: KEYWORD_CATEGORIES[key],
        }
      })
    })

    onMounted(() => {
      // カテゴリーの初期選択（カテゴリ指定なしの場合の初期値選択）
      if (!state.selectedCategory.id) {
        state.selectedCategory.id = Number(Object.keys(KEYWORD_CATEGORIES)[0])
      }
    })

    /**
     * カテゴリセレクタで選択されたカテゴリーへ遷移
     * @param categoryId カテゴリーセレクターで選択された用語カテゴリー ID
     */
    const navigateTo = (categoryId: number | string | undefined): void => {
      // 遷移完了までスクリーンロック
      screenLock.lockScreen({
        hasBlackOut: false,
        hasBlur: false,
        hasWaitAnimation: false,
      })

      const url = new URL(location.href)
      const QUERY_PARAM_KEY = 'filter' as const

      // カテゴリ切り替え時に古いページ番号が残ると、新カテゴリでは存在しないページを
      // 開いてしまう可能性があるため、ページネーションを1ページ目にリセットする
      url.searchParams.delete(PAGINATION_QUERY)

      // 製品カテゴリのクエリパラメータ生成
      if (categoryId) {
        url.searchParams.set(QUERY_PARAM_KEY, categoryId.toString())
      } else {
        url.searchParams.delete(QUERY_PARAM_KEY)
      }

      location.assign(url)
    }

    return {
      // constants
      KEYWORD_CATEGORIES,
      PAGINATION_QUERY,

      // store
      mediaQueryStore,

      // routes
      keywordPath,

      // data
      state,

      // computed
      pagination,
      categorySelectorItems,

      // methods
      navigateTo,
    }
  },
})
</script>

<template>
  <section class="section">
    <p class="p">
      あなたのお悩み解決に役立つ用語解説集です。<br>
      気になる用語をマイページにストックできる「フォロー機能」も、ぜひご活用ください。
    </p>

    <FollowGuide :has-accordion="true" />

    <div class="o-list__record">
      <dl class="o-list__record--unit">
        <dt class="o-list__record--term">
          カテゴリを選択する
        </dt>

        <dd class="o-list__record--description">
          <SelectorLite
            v-model="state.selectedCategory.id"
            :items="categorySelectorItems"
            :inline="!mediaQueryStore.context.isUnderPhablet"
            @change="navigateTo($event)" />
        </dd>
      </dl>
    <!-- /.o-list__record --></div>

    <SectionHeadingDefault>
      {{ state.selectedCategory.label }}
    </SectionHeadingDefault>

    <p class="p">
      <TextTruncationMultiLine :max-line="3">
        {{ state.selectedCategory.description }}
      </TextTruncationMultiLine>
    </p>
    <p
      v-if="state.selectedCategory.id"
      class="p">
      <a
        class="a"
        :href="keywordPath({ id: state.selectedCategory.id?.toString() })">
        詳細を見る
      </a>
    </p>

    <template v-if="state.trendFilterKeywords.length">
      <h3 class="h3 u-text__large u-text__bold">
        ピックアップ
      </h3>

      <div class="o-list__trendFilterKeywords">
        <ul class="o-list__trendFilterKeywords--items">
          <li
            v-for="item in state.trendFilterKeywords"
            :key="item.id"
            class="o-list__trendFilterKeywords--item">
            <p class="p u-text__xx-large">
              <TextTruncationSingleLine>
                {{ item.title }}
              </TextTruncationSingleLine>
            </p>

            <ButtonDefault
              :href="keywordPath({ id: item.id.toString() })"
              is-font-small
              narrow-margin
              color="orange">
              詳細を見る
            </ButtonDefault>
          </li>
        </ul>
      <!-- /.o-list__trendFilterKeywords --></div>
    </template>

    <h3 class="h3 u-text__large u-text__bold">
      {{ state.selectedCategory.label }}の関連用語
      <span class="span u-text__small">
        ({{ state.pagination.totalCount }})
      </span>
    </h3>

    <div
      class="o-list__cols is-col2"
      data-bullet="circle">
      <ul class="o-list__cols--items">
        <li
          v-for="item in state.selectedCategory.items"
          :key="item.id"
          class="o-list__cols--item">
          <a
            class="a u-text__black"
            :href="keywordPath({ id: item.id.toString() })">
            {{ item.label }}
          </a>
        </li>
      </ul>
    <!-- /.o-list__cols --></div>

    <template v-if="pagination.totalPage">
      <PaginationDefault
        :page="pagination.page"
        :offset="4"
        :total-page="pagination.totalPage || 0" />
    </template>
  </section>
</template>

<style lang="sass" scoped>
.o-list__trendFilterKeywords
  $root: &
  $item-gap-px: 12
  $visible-cols: 3

  & &--items
    display: grid
    grid-template-columns: repeat($visible-cols, minmax(0, 1fr))
    gap: #{$item-gap-px}px getRelativeValue($item-gap-px, $primary-col-width)

  & &--item
    padding: changeSizePx2Em(20)
    background-color: $bg-x-light-orange

  +breakpoint
    overflow-x: auto

    #{$root}--items
      display: flex
      flex: 1 0 auto
      gap: #{$item-gap-px}px

    #{$root}--item
      $item-width-px: math.div($primary-col-width - $item-gap-px * ($visible-cols - 1), $visible-cols)
      min-width: #{$item-width-px}px
</style>
